// ✅ src/pages/Sales/PartPaymentCustomer.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

// ✅ currency formatter
const formatAmount = (amount) => {
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(n);
};

const getPaymentStatusColor = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "overdue":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const PartPaymentCustomer = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // Search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubType: "",
    doB: "",
    paymentStatus: "",
  });

  // dropdown from API
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const paymentStatuses = ["Completed", "Pending", "Processing", "Overdue"];

  // table data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // checkbox selection
  const [selected, setSelected] = useState({});
  const selectedKeys = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );

  // API endpoints
  const api = {
    assetSubTypes: `${BASE_URL}/api/partListing/assetSubTypes`,
    search: `${BASE_URL}/api/partListing/search`,
    deleteSelected: `${BASE_URL}/api/partListing/deleteSelected`,
  };

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddNew = () => {
    navigate("/Sales/PartPaymentEntry");
    toast.info("Opening new part payment entry...");
  };

  // ---------- Load dropdown ----------
  useEffect(() => {
    const loadAssetSubTypes = async () => {
      try {
        const res = await axios.get(api.assetSubTypes, {
          headers: commonHeaders,
        });
        setAssetSubTypes(res.data?.data || []);
      } catch (err) {
        console.error("Asset Sub Types Load Error:", err);
        toast.error("Failed to load asset sub types");
      }
    };

    loadAssetSubTypes();
  }, [api.assetSubTypes, commonHeaders]);

  // ---------- Search ----------
  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {
        enquiryNumber: searchData.enquiryNumber || "",
        customerName: searchData.customerName || "",
        customerId: searchData.customerId || "",
        assetSubType: searchData.assetSubType || "",
        doB: searchData.doB || "",
        paymentStatus: searchData.paymentStatus || "",
      };

      const res = await axios.get(api.search, {
        params,
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      console.log("search:", data);

      const mapped = data.map((r, idx) => ({
        key: String(r.fld_sales_leadid ?? `${r.fld_customerid}-${idx}`),
        slNo: idx + 1,
        customerId: r.fld_customerid,
        customerName: r.fld_customername,
        plotNumber: r.fld_plotnumber,
        assetCode: r.fld_assetcode || r.fld_plotassetcode,
        assetTypeModel: r.fld_assetsubtypename,
        amount: r.fld_payingamount,
        balAmount: r.fld_balanceamount,
        paymentStatus: r.fld_paystatus,
        enquiryNumber: r.fld_enquirnum,
        leadId: r.fld_sales_leadid,
      }));

      setRows(mapped);
      setSelected({});
      toast.success(`Found ${mapped.length} record(s)`);
    } catch (err) {
      console.error("Search Error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Row click -> open entry page ----------
  const openEntry = (row) => {
    navigate(`/Sales/PartPaymentEntry?id=${row.customerId}&leadId=${row.leadId}`);
  };

  // ---------- Checkboxes ----------
  const toggleAll = (checked) => {
    const next = {};
    if (checked) {
      rows.forEach((r) => {
        next[r.key] = true;
      });
    }
    setSelected(next);
  };

  const toggleOne = (key, checked) => {
    setSelected((prev) => ({ ...prev, [key]: checked }));
  };

  // ---------- Update ----------
  const handleUpdate = () => {
    if (selectedKeys.length !== 1) {
      toast.warning("Please select exactly ONE row to update");
      return;
    }
    const row = rows.find((r) => r.key === selectedKeys[0]);
    if (!row) return;
    openEntry(row);
  };

  // ---------- Delete ----------
  const handleDelete = async () => {
    if (selectedKeys.length === 0) {
      toast.warning("Please select at least one row to delete");
      return;
    }

    const selectedRows = rows.filter((r) => selected[r.key]);
    const ids = selectedRows.map((r) => r.customerId);

    try {
      await axios.delete(api.deleteSelected, {
        headers: commonHeaders,
        data: { ids },
      });
      toast.success("Deleted successfully");
      await handleSearch();
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(
        err?.response?.data?.message || "Delete failed (backend not added?)"
      );
    }
  };

  // ---------- Summary ----------
  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const totalBalance = rows.reduce(
    (sum, r) => sum + Number(r.balAmount || 0),
    0
  );
  const pendingCount = rows.filter(
    (r) => String(r.paymentStatus || "").toLowerCase() === "pending"
  ).length;

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PART PAYMENT CUSTOMER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header Section */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Part Payment Customer
              </h1>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={handleUpdate}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-blue-700 hover:bg-blue-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FaEdit />
                Update
              </button>

              <button
                onClick={handleDelete}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <FaTrash />
                Delete
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Search Filters Section */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Search Criteria
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Enquiry Number
                  </label>
                  <input
                    type="text"
                    placeholder="Enter enquiry number"
                    value={searchData.enquiryNumber}
                    onChange={(e) => handleInputChange("enquiryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaUser className="inline mr-2" />
                    Customer Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={searchData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Customer ID
                  </label>
                  <input
                    type="text"
                    placeholder="Enter customer ID"
                    value={searchData.customerId}
                    onChange={(e) => handleInputChange("customerId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>
                  <select
                    value={searchData.assetSubType}
                    onChange={(e) => handleInputChange("assetSubType", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {assetSubTypes.map((x) => (
                      <option key={x.fld_assetsubtypeid} value={x.fld_assetsubtypeid}>
                        {x.fld_assetsubtypename}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>
                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Payment Status
                  </label>
                  <select
                    value={searchData.paymentStatus}
                    onChange={(e) => handleInputChange("paymentStatus", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {paymentStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? `${buttonClass} ${hoverClass} text-white`
                      : `${buttonClass} ${hoverClass} text-white`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {/* Results Table */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedKeys.length === rows.length}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Plot Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Type / Model</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Bal Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Payment Status</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className={`px-6 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
                    >
                      No data. Click Search.
                    </td>
                  </tr>
                ) : (
                  rows.map((customer, index) => (
                    <tr
                      key={customer.key}
                      className={`${
                        index % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors cursor-pointer`}
                      onClick={() => openEntry(customer)}
                    >
                      <td
                        className="px-4 py-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!!selected[customer.key]}
                          onChange={(e) => toggleOne(customer.key, e.target.checked)}
                        />
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {customer.slNo}
                      </td>

                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        {customer.customerId}
                      </td>

                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                        {customer.customerName}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {customer.plotNumber}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {customer.assetCode}
                      </td>

                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {customer.assetTypeModel}
                      </td>

                      <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                        {formatAmount(customer.amount)}
                      </td>

                      <td
                        className={`px-6 py-4 text-sm font-semibold ${
                          Number(customer.balAmount || 0) > 0
                            ? isDark
                              ? "text-red-400"
                              : "text-red-600"
                            : isDark
                            ? "text-green-400"
                            : "text-green-600"
                        }`}
                      >
                        {formatAmount(customer.balAmount)}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                            customer.paymentStatus
                          )}`}
                        >
                          {customer.paymentStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Results Summary */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Search Results
                </h4>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Showing {rows.length} part payment customers
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className={`p-3 rounded-lg ${isDark ? "bg-green-900/30" : "bg-green-50"}`}>
                  <div className="text-lg font-bold text-green-600">{formatAmount(totalAmount)}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total Amount</div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-red-900/30" : "bg-red-50"}`}>
                  <div className="text-lg font-bold text-red-600">{formatAmount(totalBalance)}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Balance Amount</div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-yellow-900/30" : "bg-yellow-50"}`}>
                  <div className="text-lg font-bold text-yellow-600">{pendingCount}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Pending</div>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default PartPaymentCustomer;